/**
 * Seniorities table component for organization structure management.
 *
 * This component displays seniorities in a tabular format with their
 * associated member counts and actions. It provides functionality for
 * adding new seniorities through an inline button interface.
 *
 * Key Features:
 * - Displays seniorities with name, member count, and delete action columns
 * - Supports adding new seniorities via button with proper empty state
 * - Shows appropriate empty states when no seniorities are present
 * - Integrates with React Table for consistent table rendering
 * - Provides user-friendly interface for seniority management
 *
 * State Management:
 * - Empty state: Shows message and add seniority button when no seniorities exist
 * - Populated state: Shows table with data and add seniority row at bottom
 *
 * The component uses a "last row children" pattern to render additional UI
 * elements (empty state, add seniority controls) as table rows for consistent styling.
 */
import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { CirclePlus, LoaderIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table/data-table';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';
import { Button } from '@/components/ui/button';
import { SeniorityFormData } from '@/modules/settings/types/seniority';

/**
 * Props interface for the SenioritiesTable component.
 * Includes table configuration, interaction handlers, and state flags.
 */
interface SenioritiesTableProps {
  /** Configured React Table instance with seniority data and columns */
  table: Table<SeniorityFormData>;
  /** Callback when add seniority button is clicked */
  onAddSeniority: () => void;
  /** Whether the table currently has any seniorities */
  hasSeniorities: boolean;
  /** Whether data is currently being loaded */
  isLoading?: boolean;
}

/**
 * Seniorities table component with integrated seniority management.
 *
 * This component orchestrates the display of seniorities in a table format while
 * providing contextual UI for adding new seniorities. It adapts its presentation based
 * on current state (empty vs populated).
 *
 * Rendering Logic:
 * - Empty state: Full-height message with prominent add seniority button
 * - Populated state: Data table with add seniority row at bottom
 *
 * The component uses the DataTable's "lastRowChildren" pattern to seamlessly
 * integrate additional UI elements as table rows, maintaining consistent styling
 * and layout with the rest of the table.
 */
export const SenioritiesTable: React.FC<SenioritiesTableProps> = ({
  table,
  onAddSeniority,
  hasSeniorities,
  isLoading = false,
}) => {
  const { t } = useTranslation(['authenticated.settings', 'common']);

  /**
   * Renders the loading state when data is being fetched.
   * Shows a centered loading spinner within the table structure.
   */
  const renderLoadingState = (): React.ReactNode => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={table.getAllColumns().length} className="h-32 px-0">
            <StickyTableStateContent align="center" className="px-4 py-3">
              <LoaderIcon className="h-4 w-4 animate-spin text-foreground opacity-50" />
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }
    return null;
  };

  /**
   * Renders the empty state when no seniorities are present.
   * Shows a left-aligned message with prominent add seniority button.
   * Only displayed when hasSeniorities is false and not loading.
   */
  const renderEmptyState = (): React.ReactNode => {
    if (!hasSeniorities && !isLoading) {
      return (
        <tr>
          <td colSpan={table.getAllColumns().length} className="px-0 py-4">
            <StickyTableStateContent className="items-start gap-3 px-8 text-left">
              <div className="flex flex-col items-start gap-0.5">
                <div className="text-sm font-semibold text-foreground">
                  {t('pages.seniorities.emptyState.title')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('pages.seniorities.emptyState.description')}
                </div>
              </div>
              <Button type="button" onClick={onAddSeniority} className="h-10 gap-2">
                <CirclePlus className="h-4 w-4" />
                {t('pages.seniorities.addSeniority')}
              </Button>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  /**
   * Renders the add seniority row when seniorities exist.
   * Provides a compact add seniority interface at the bottom of the table.
   * Only shown when seniorities are present.
   */
  const renderAddSeniorityRow = (): React.ReactNode => {
    if (hasSeniorities) {
      return (
        <tr className="hover:bg-transparent">
          <td colSpan={table.getAllColumns().length} className="h-16 px-4">
            <div className="flex justify-start">
              <Button
                type="button"
                variant="ghost"
                onClick={onAddSeniority}
                className="gap-2"
                size="sm"
              >
                <CirclePlus className="h-4 w-4" />
                {t('pages.seniorities.addSeniority')}
              </Button>
            </div>
          </td>
        </tr>
      );
    }

    return null;
  };

  // Combine all conditional row elements for DataTable integration
  const lastRowChildren = (
    <>
      {renderLoadingState()}
      {renderEmptyState()}
      {renderAddSeniorityRow()}
    </>
  );

  return <DataTable table={table} lastRowChildren={lastRowChildren} />;
};
